// [ai] Widgets inside a document: the server turns a ```atlas fence into <atlas-widget data-spec> and a ::name{k=v} directive
// into <atlas-name k="v"> (app/lib/render.py); the Doc page hands each element here for the spec its frame renders. Only
// catalog widgets embed (a document has no folder for files); the old directive attributes map onto the catalog's props.
import type { WidgetSpec } from "./types";
type Map = { [attr: string]: string };
const ATTRS: Record<string, Map> = { stat: { of: "path", label: "label" } };   // ::stat{of=hours label="…"} was the dashboard page's syntax
const num = (v: string) => (/^-?\d+(\.\d+)?$/.test(v) ? Number(v) : v);

/** The specs of a document's embeds, ids made unique (a second "h" is "h~2" — one frame, one store key each). */
export function specsFromEmbeds(els: Element[]): WidgetSpec[] {
  const seen = new Set<string>();
  return els.map((el, i) => { const s = specFromEmbed(el, i); if (seen.has(s.id)) s.id = `${s.id}~${i + 1}`; seen.add(s.id); return s; });
}
export function specFromEmbed(el: Element, i: number): WidgetSpec {
  const id = `embed-${i + 1}`; const name = el.tagName.toLowerCase().replace(/^atlas-/, "");
  if (name === "widget") {
    const err = el.getAttribute("data-error"); if (err) return { id, kind: "catalog", error: err };
    let raw: unknown;
    try { raw = JSON.parse(el.getAttribute("data-spec") ?? ""); } catch (e) { return { id, kind: "catalog", error: `atlas block is not JSON: ${(e as Error).message}` }; }
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) return { id, kind: "catalog", error: "atlas block: a widget object is expected" };
    const o = raw as Record<string, unknown>; const kind = o.kind === "vega" ? "catalog" : o.kind;
    if (kind !== "catalog") return { id, kind: "catalog", error: "only catalog widgets embed in a document" };
    const spec: WidgetSpec = { id: typeof o.id === "string" && o.id ? o.id : id, kind: "catalog", type: o.kind === "vega" ? "vega" : (typeof o.type === "string" ? o.type : undefined) };
    if (typeof o.title === "string") spec.title = o.title;
    if (o.query && typeof o.query === "object") spec.query = o.query;
    if (o.props && typeof o.props === "object" && !Array.isArray(o.props)) spec.props = o.props as Record<string, unknown>;
    return spec;
  }
  const map = ATTRS[name] ?? {}; const props: Record<string, unknown> = {}; let title: string | undefined;
  for (const a of Array.from(el.attributes)) { const k = map[a.name] ?? a.name; props[k] = num(a.value); if (a.name === "label") title = a.value; }
  return { id, kind: "catalog", type: name, ...(title ? { title } : {}), ...(Object.keys(props).length ? { props } : {}) };
}
